// ============================================
// COMPONENT - Sidebar
// ============================================

import { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { useUIStore, useAuthStore, useDashboardKpis, usePedidosPendientesCount, useMovimientosPendientesCount } from '@almacenes/core';
import { cn } from '@almacenes/ui';
import { 
  MdDashboard, 
  MdInventory, 
  MdShoppingCart, 
  MdReceipt, 
  MdSettings, 
  MdStar, 
  MdVideoLibrary, 
  MdStorefront,
  MdKeyboardArrowRight,
} from 'react-icons/md';
import { FaHandHoldingDollar, FaWhatsapp, FaCashRegister } from 'react-icons/fa6';
import { HiOutlineClipboardDocumentList } from "react-icons/hi2";
import { IoStorefrontOutline } from "react-icons/io5";
import { PiChartLineUpBold } from "react-icons/pi";
import { openExternalLink } from '../lib/premium';

const allNavigation = [
  { name: 'Panel', href: '/dashboard', icon: MdDashboard, roles: ['propietario'] },
  { name: 'Venta', href: '/ventas', icon: MdShoppingCart, roles: ['propietario', 'vendedor'] },
  { name: 'Inventario', href: '/inventario', icon: MdInventory, roles: ['propietario'] },
  { name: 'Fiados', href: '/fiados', icon: FaHandHoldingDollar, roles: ['propietario'] },
  { name: 'Mi Negocio Pro', href: '/mi-negocio', icon: IoStorefrontOutline, roles: ['propietario'] },
  { 
    name: 'Gestión', 
    icon: HiOutlineClipboardDocumentList, 
    roles: ['propietario'],
    submenu: [
      /* TODO: Camilo lo habilitara cuando ya tenga listo el modulo de facturacion
      { name: 'Facturación', href: '/facturacion', icon: FaFileInvoice, roles: ['propietario'] },*/
      { name: 'Boletas', href: '/detalle-boleta', icon: MdReceipt, roles: ['propietario'] },
      { name: 'Reportes', href: '/reporte', icon: PiChartLineUpBold, roles: ['propietario'] },
      { name: 'DTE', href: '/dte', icon: MdStar, roles: ['propietario'], requiresTaxpayer: true },
    ]
  },  
  {
    name: 'Negocio',
    icon: MdStorefront,
    roles: ['propietario', 'vendedor'],
    submenu: [
      { name: 'Caja 360º', href: '/caja-360', icon: FaCashRegister, roles: ['propietario', 'vendedor'] },
      { name: 'Ecommerce', href: '/ecommerce', icon: MdStorefront, roles: ['propietario'] },
    ]
  },
  { 
    name: 'Ajustes', 
    icon: MdSettings, 
    roles: ['propietario', 'vendedor'],
    submenu: [
      { name: 'Configuración', href: '/configurar', icon: MdSettings, roles: ['propietario'] },
      { name: 'Tutoriales', href: '/tutoriales', icon: MdVideoLibrary, roles: ['propietario', 'vendedor'] },
      { name: 'Contáctanos', href: 'https://wa.me/+56920201644?text=Hola+me+interesa+comunicarme+con+almacenes+digitales!', icon: FaWhatsapp, roles: ['propietario', 'vendedor'], isExternalLink: true }
    ]
  },
];

export const Sidebar = () => {
  const sidebarOpen = useUIStore((state) => state.sidebarOpen);
  const toggleSidebar = useUIStore((state) => state.toggleSidebar);
  const session = useAuthStore((state) => state.session);
  const { data: kpis } = useDashboardKpis();
  const [hoveredGroup, setHoveredGroup] = useState<string | null>(null);
  const [clickedGroup, setClickedGroup] = useState<string | null>(null);
  const [submenuPosition, setSubmenuPosition] = useState<{ top: number; left: number } | null>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
  
  // Filtrar navegación según el rol del usuario
  const esPropietario = session?.user?.esPropietario ?? false;
  const userRole = esPropietario ? 'propietario' : 'vendedor';

  const { data: pedidosPendientes } = usePedidosPendientesCount();
  const { data: movimientosCajaPendientes } = useMovimientosPendientesCount(esPropietario);

  // Obtener conteo de pedidos pendientes
  const pendingOrdersCount = kpis?.pendingOrdersKpi?.pendingCount ?? 0;

  // Cerrar el submenu clickeado cuando se hace click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        setClickedGroup(null);
      }
    };

    if (clickedGroup) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [clickedGroup]);

  // Calcular posición del submenu
  useEffect(() => {
    const activeGroup = clickedGroup || hoveredGroup;
    if (activeGroup && buttonRefs.current[activeGroup]) {
      const button = buttonRefs.current[activeGroup];
      const rect = button.getBoundingClientRect();
      setSubmenuPosition({
        top: rect.top,
        left: rect.right
      });
    } else {
      setSubmenuPosition(null);
    }
  }, [clickedGroup, hoveredGroup]);
  
  const filterItems = (items: typeof allNavigation) => {
    return items.filter(item => {
      if (!item.roles.includes(userRole)) return false;
      if ('requiresTaxpayer' in item && item.requiresTaxpayer && !session?.taxpayer) return false;
      
      // Si tiene submenu, filtrar los items del submenu
      if ('submenu' in item && item.submenu) {
        const filteredSubmenu = item.submenu.filter(subItem => {
          if (!subItem.roles.includes(userRole)) return false;
          if ('requiresTaxpayer' in subItem && subItem.requiresTaxpayer && !session?.taxpayer) return false;
          return true;
        });
        // Solo mostrar el grupo si tiene al menos un item visible
        return filteredSubmenu.length > 0;
      }
      
      return true;
    });
  };

  const navigation = filterItems(allNavigation);

  const handleExternalLink = (href: string) => {
    if (window.electronAPI) {
      openExternalLink(href);
    } else {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  };

  const getActiveGroup = (itemName: string) => {
    return clickedGroup === itemName || hoveredGroup === itemName;
  };

  const handleSubmenuClick = () => {
    setClickedGroup(null);
    setHoveredGroup(null);
  };

  const closeSidebarOnMobile = () => {
    // Cerrar sidebar solo en móvil/tablet (< 1024px que es el breakpoint lg)
    if (window.innerWidth < 1024 && sidebarOpen) {
      toggleSidebar();
    }
  };

  const handleMenuItemClick = () => {
    handleSubmenuClick();
    closeSidebarOnMobile();
  };

  // Verificar si un grupo tiene notificaciones
  const hasGroupNotifications = (groupName: string) => {
    if (groupName === 'Negocio' && (pendingOrdersCount > 0 || (movimientosCajaPendientes ?? 0) > 0)) {
      return true;
    }
    return false;
  };

  // Total combinado para el badge del grupo "Negocio" (pedidos ecommerce + movimientos de caja)
  const negocioNotificationsCount = pendingOrdersCount + (movimientosCajaPendientes ?? 0);

  return (
    <aside
      ref={sidebarRef}
      className={cn(
        'fixed inset-y-0 left-0 z-30 w-16 transform transition-transform duration-300 ease-in-out lg:static lg:translate-x-0',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      )}
      style={{ backgroundColor: '#11194F' }}
    >
      {/* Logo */}
      <div className="flex h-16 items-center justify-center border-b" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
        {session?.almacen?.logoUrl ? (
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden" 
            style={{ 
              backgroundColor: session.almacen.colorFondo || '#FFFFFF',
              padding: '6px'
            }}
          >
            <img
              src={session.almacen.logoUrl}
              alt="Logo del almacén"
              className="object-contain w-full h-full"
            />
          </div>
        ) : (
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FF6B35' }}>
            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
              <polyline points="7.5 4.21 12 6.81 16.5 4.21" />
              <polyline points="7.5 19.79 7.5 14.6 3 12" />
              <polyline points="21 12 16.5 14.6 16.5 19.79" />
              <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
              <line x1="12" y1="22.08" x2="12" y2="12" />
            </svg>
          </div>
        )}
      </div>

      <nav className="mt-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 5rem)' }}>
        <div className="space-y-1">
          {navigation.map((item) => {
            const Icon = item.icon;
            const showSubmenu = getActiveGroup(item.name);
            
            // Item con submenu
            if ('submenu' in item && item.submenu) {
              const filteredSubmenu = item.submenu.filter(subItem => {
                if (!subItem.roles.includes(userRole)) return false;
                if ('requiresTaxpayer' in subItem && subItem.requiresTaxpayer && !session?.taxpayer) return false;
                return true;
              });

              return (
                <div 
                  key={item.name} 
                  className="relative"
                >
                  <button
                    ref={(el) => { buttonRefs.current[item.name] = el; }}
                    onMouseEnter={() => setHoveredGroup(item.name)}
                    onMouseLeave={() => setHoveredGroup(null)}
                    onClick={() => setClickedGroup(clickedGroup === item.name ? null : item.name)}
                    className={cn(
                      "group w-full flex flex-col items-center justify-center rounded-lg px-2 py-3 text-xs font-medium transition-all",
                      showSubmenu ? "text-white bg-white/10" : "text-gray-400 hover:text-white hover:bg-white/5"
                    )}
                    title={item.name}
                  >
                    <div className="relative">
                      <Icon className="text-2xl mb-1" />
                      <MdKeyboardArrowRight className="absolute -right-4 top-1 text-sm" />
                      {/* Badge de notificación en el grupo */}
                      {hasGroupNotifications(item.name) && (
                        <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                          {negocioNotificationsCount > 9 ? '9+' : negocioNotificationsCount}
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] leading-tight text-center">{item.name}</span>
                  </button>
                  
                  {/* Submenu flotante - renderizado en portal fijo */}
                  {showSubmenu && submenuPosition && (
                    <div 
                      className="fixed min-w-[200px] rounded-lg shadow-2xl border border-white/20"
                      style={{ 
                        backgroundColor: '#1a2150',
                        top: `${submenuPosition.top}px`,
                        left: `${submenuPosition.left}px`,
                        zIndex: 9999
                      }}
                      onMouseEnter={() => setHoveredGroup(item.name)}
                      onMouseLeave={() => setHoveredGroup(null)}
                    >
                      <div className="py-3 px-3">
                        <h3 className="text-white font-semibold text-sm mb-3 pb-2 border-b border-white/10">
                          {item.name}
                        </h3>
                        <div className="space-y-1">
                          {filteredSubmenu.map((subItem) => {
                            const SubIcon = subItem.icon;
                            
                            if ('isExternalLink' in subItem && subItem.isExternalLink) {
                              return (
                                <button
                                  key={subItem.name}
                                  onClick={() => {
                                    handleExternalLink(subItem.href);
                                    handleMenuItemClick();
                                  }}
                                  className="group w-full flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-all text-gray-300 hover:text-white hover:bg-white/10"
                                >
                                  <SubIcon className="text-lg flex-shrink-0" />
                                  <span className="whitespace-nowrap">{subItem.name}</span>
                                </button>
                              );
                            }
                            
                            return (
                              <NavLink
                                key={subItem.name}
                                to={subItem.href}
                                onClick={handleMenuItemClick}
                                className={({ isActive }) =>
                                  cn(
                                    'group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-all',
                                    isActive
                                      ? 'text-white bg-white/20'
                                      : 'text-gray-300 hover:text-white hover:bg-white/10'
                                  )
                                }
                              >
                                <div className="relative flex-shrink-0">
                                  <SubIcon className="text-lg" />
                                  {/* Badge "NEW" para Caja 360º */}
                                  {subItem.name === 'Caja 360º' && (
                                    <span className="new-badge bg-gradient-to-r from-purple-500 to-pink-500 text-white absolute -top-2 -right-2">
                                      NEW
                                    </span>
                                  )}
                                </div>
                                <span className="whitespace-nowrap">{subItem.name}</span>
                                {/* Badge de notificación para Ecommerce */}
                                {subItem.name === 'Ecommerce' && pendingOrdersCount > 0 && (
                                  <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                                    {pendingOrdersCount}
                                  </span>
                                )}
                                {/* Badge de notificación para Caja 360º: movimientos pendientes de aprobación (propietario) */}
                                {subItem.name === 'Caja 360º' && esPropietario && (movimientosCajaPendientes ?? 0) > 0 && (
                                  <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                                    {(movimientosCajaPendientes ?? 0) > 9 ? '9+' : movimientosCajaPendientes}
                                  </span>
                                )}
                              </NavLink>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            }
            
            // Item simple (sin submenu)
            if ('isExternalLink' in item && item.isExternalLink) {
              return (
                <button
                  key={item.name}
                  onClick={() => {
                    handleExternalLink(item.href!);
                    closeSidebarOnMobile();
                  }}
                  className="group w-full flex flex-col items-center justify-center rounded-lg px-2 py-3 text-xs font-medium transition-all text-gray-400 hover:text-white hover:bg-white/5"
                  title={item.name}
                >
                  <Icon className="text-2xl mb-1" />
                  <span className="text-[10px] leading-tight text-center">{item.name}</span>
                </button>
              );
            }
            
            const isEcommerce = item.href === '/ecommerce';
            const showBadge = isEcommerce && esPropietario && (pedidosPendientes ?? 0) > 0;

            return (
              <NavLink
                key={item.name}
                to={item.href!}
                onClick={closeSidebarOnMobile}
                className={({ isActive }) =>
                  cn(
                    'group flex flex-col items-center justify-center rounded-lg px-2 py-3 text-xs font-medium transition-all',
                    isActive
                      ? 'text-white'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  )
                }
                title={item.name}
              >
                <div className="relative">
                  <Icon className="text-2xl mb-1" />
                </div>
                {/* Badge de notificación para Ecommerce */}
                <div className="relative mb-1">
                  {showBadge && (
                    <span className="absolute -top-1 -right-1.5 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[14px] h-3.5 flex items-center justify-center px-0.5 leading-none">
                      {(pedidosPendientes ?? 0) > 9 ? '9+' : pedidosPendientes}
                    </span>
                  )}
                </div>
                <span className="text-[10px] leading-tight text-center">{item.name}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>
    </aside>
  );
};
